import { Button, Flex, FormLayout, Modal, statesByCountry, getCountryList, type CountryCode } from "@wayflyer/flyui";
import { RhfAutocompleteField, RhfDropdownField, RhfTextField } from "@wayflyer/flyui-hook-form";
import { FormProvider, useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useStateRegion, getJurisdictionalLabelGetters, type SupportedCountry } from "shared-ui/kyc";
import { IconMapPin16Line } from "@wayflyer/flyui-icons/16/line";

import type { ParsedAddress, PlaceDetails } from "../../hooks/use-places-autocomplete/google-places";

import { useEditableAddressSearch, type EditableAddressFormValues } from "./use-editable-address-search";

interface FieldMapping {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  country_code: string;
}

interface EditableAddressSearchFieldProps {
  form: UseFormReturn<any>;
  handleAddressSelect: (address: ParsedAddress, details: PlaceDetails, mainText?: string) => void;
  label?: string;
  fieldName?: string;
  clearable?: boolean;
  countries?: CountryCode[];
  states?: string[];
  placeholder?: string;
  parentFormFieldName?: string;
  required?: boolean;
  onBlur?: () => void;
  onAddressChange?: (optionId: string | number | null) => void;
  fieldMapping?: FieldMapping;
  lockFields?: Partial<EditableAddressFormValues>;
}

const usFiveDigitZipRegex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;

const addressModalSchema = Yup.object().shape({
  country_code: Yup.string().required("Country is required"),
  address_line_1: Yup.string().required("Address line 1 is required"),
  address_line_2: Yup.string().nullable(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zip_code: Yup.string()
    .required("ZIP code is required")
    .test("zip-code-format", "Please enter a valid 5-digit ZIP code", function (value) {
      const { country_code } = this.parent;
      if (country_code === "US") {
        if (!value) {
          return false;
        }
        return usFiveDigitZipRegex.test(value);
      }
      return true;
    }),
});

export const EditableAddressSearchField = ({
  form,
  handleAddressSelect,
  label = "Address finder",
  placeholder = "Search for address",
  fieldName = "address_search",
  clearable = false,
  required = false,
  countries,
  states,
  parentFormFieldName,
  onBlur,
  onAddressChange,
  fieldMapping,
  lockFields,
}: EditableAddressSearchFieldProps) => {
  const modalForm = useForm<EditableAddressFormValues>({
    resolver: yupResolver(addressModalSchema),
    defaultValues: {
      country_code: "US",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      zip_code: "",
    },
    mode: "all",
  });

  const modalCountryCode = useWatch({
    control: modalForm.control,
    name: "country_code",
  });

  useEffect(() => {
    // Clear and re-validate ZIP code when country changes
    modalForm.clearErrors("zip_code");
    if (modalForm.formState.dirtyFields.zip_code) {
      void modalForm.trigger("zip_code");
    }
  }, [modalCountryCode, modalForm]);

  const handleSaveAddress = async () => {
    const isValid = await modalForm.trigger();
    if (!isValid) {
      return;
    }

    const values = modalForm.getValues();
    handleModalSave(values);
  };

  const handleAutocompleteBlur = () => {
    const searchTerm = addressAutocomplete.searchTerm;

    if (isOpeningManualEntryRef.current) {
      isOpeningManualEntryRef.current = false;
      form.clearErrors(fieldName);
      return;
    }

    if (!isOptionId && searchTerm) {
      form.setValue(fieldName, searchTerm, {
        shouldDirty: true,
      });
      form.clearErrors(fieldName);
    }
    onBlur?.();
  };

  const handleManualEntryClick = () => {
    isOpeningManualEntryRef.current = true;
    handleManualEntry(true);
  };

  const {
    addressAutocomplete,
    transformedOptions,
    handleAddressChange: internalHandleAddressChange,
    isManualEntryModalOpen,
    manualEntryAddress,
    handleManualEntry,
    handleModalSave,
    handleModalCancel,
    createableConfig,
  } = useEditableAddressSearch({
    form,
    handleAddressSelect,
    fieldName,
    countries,
    states,
    onManualEntry: handleManualEntryClick,
    fieldMapping,
    parentFormFieldName,
  });

  const handleAddressChange = useCallback(
    (optionId: string | number | null) => {
      if (optionId === null) {
        const currentSearchValue = form.getValues(fieldName);
        if (currentSearchValue) {
          internalHandleAddressChange(optionId);
          onAddressChange?.(optionId);
        } else {
          form.setValue(fieldName, "", { shouldDirty: true });
          addressAutocomplete.clear();
          if (fieldMapping) {
            form.setValue(fieldMapping.address_line_1, "", { shouldDirty: true });
            form.setValue(fieldMapping.address_line_2, "", { shouldDirty: true });
            form.setValue(fieldMapping.city, "", { shouldDirty: true });
            form.setValue(fieldMapping.state, "", { shouldDirty: true });
            form.setValue(fieldMapping.zip_code, "", { shouldDirty: true });
            form.setValue(fieldMapping.country_code, "", { shouldDirty: true });
          }
          if (parentFormFieldName) {
            form.resetField(parentFormFieldName);
          }
        }
        return;
      }

      internalHandleAddressChange(optionId);
      onAddressChange?.(optionId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [internalHandleAddressChange, onAddressChange, fieldName, fieldMapping, parentFormFieldName]
  );

  useEffect(() => {
    if (isManualEntryModalOpen && manualEntryAddress) {
      modalForm.reset({
        country_code: lockFields?.country_code ?? (manualEntryAddress?.country_code || ""),
        address_line_1: manualEntryAddress.address_line_1 || "",
        address_line_2: manualEntryAddress.address_line_2 || "",
        city: lockFields?.city ?? (manualEntryAddress.city || ""),
        state: lockFields?.state ?? (manualEntryAddress.state || ""),
        zip_code: lockFields?.zip_code ?? (manualEntryAddress.zip_code || ""),
      });
    }
  }, [isManualEntryModalOpen, manualEntryAddress, modalForm, lockFields]);

  const isOpeningManualEntryRef = useRef(false);

  const formFieldValue = useWatch({ control: form.control, name: fieldName });

  const isOptionId = useMemo(() => {
    if (!formFieldValue || typeof formFieldValue !== "string") {
      return false;
    }
    return transformedOptions.some((opt) => opt.id === formFieldValue);
  }, [transformedOptions, formFieldValue]);

  const optionsWithManualEntry = useMemo(() => {
    if (!formFieldValue || typeof formFieldValue !== "string" || isOptionId) {
      return transformedOptions;
    }

    return [
      {
        id: formFieldValue,
        text: formFieldValue,
        subText:
          addressAutocomplete.selectedAddress?.city && addressAutocomplete.selectedAddress?.state
            ? `${addressAutocomplete.selectedAddress.city}, ${addressAutocomplete.selectedAddress.state}`
            : "",
        icon: <IconMapPin16Line />,
      },
      ...transformedOptions,
    ];
  }, [transformedOptions, formFieldValue, isOptionId, addressAutocomplete.selectedAddress]);

  const manualEntryCountryOptions = useMemo(() => {
    return getCountryList(countries as CountryCode[]);
  }, [countries]);

  const countryCode = modalForm.watch("country_code");
  const countryStates = statesByCountry[countryCode] ?? [];
  const stateRegion = useStateRegion(countryCode as SupportedCountry);
  const stateLabel = stateRegion?.label || "State";

  const { getPostCodeLabel } = getJurisdictionalLabelGetters();
  const postalCodeLabel = getPostCodeLabel(countryCode as SupportedCountry);
  return (
    <>
      <Flex direction="column" gap="0" align="flex-start" minWidth="100%" maxWidth="100%">
        <RhfAutocompleteField
          form={form}
          required={required}
          name={fieldName}
          label={label}
          placeholder={placeholder}
          options={optionsWithManualEntry}
          externalOptions
          fetching={addressAutocomplete.fetching}
          onSearchTermChange={addressAutocomplete.onSearchTermChange}
          onChange={handleAddressChange}
          onBlur={handleAutocompleteBlur}
          clearable={clearable || undefined}
          size="large"
          createAction={createableConfig}
        />
        <Button
          variant="TertiaryAccent"
          onPointerDown={() => (isOpeningManualEntryRef.current = true)}
          onClick={handleManualEntryClick}
          type="button"
        >
          Enter manually
        </Button>
      </Flex>

      <Modal isOpen={isManualEntryModalOpen} onClose={handleModalCancel} size="normal" title="Enter address manually">
        <FormProvider {...modalForm}>
          <Flex justify="center" align="center" gap="6" direction="column" minWidth="100%">
            <Flex width="100%" direction="column" gap="4">
              <FormLayout>
                <RhfDropdownField
                  form={modalForm}
                  name="country_code"
                  required={true}
                  label="Country"
                  options={manualEntryCountryOptions}
                  disabled={!!lockFields?.country_code}
                />
                <RhfTextField form={modalForm} name="address_line_1" label="Address line 1" required={true} />
                <RhfTextField form={modalForm} name="address_line_2" label="Address line 2" />
                <RhfTextField form={modalForm} name="city" label="City" required={true} />
                {countryStates.length > 0 ? (
                  <RhfDropdownField
                    form={modalForm}
                    name="state"
                    required={true}
                    label={`${stateLabel}`}
                    options={countryStates}
                    disabled={!!lockFields?.state}
                  />
                ) : (
                  <RhfTextField
                    form={modalForm}
                    name="state"
                    label={`${stateLabel}`}
                    required={true}
                    disabled={!!lockFields?.state}
                  />
                )}
                <RhfTextField
                  form={modalForm}
                  name="zip_code"
                  required={true}
                  label={`${postalCodeLabel}`}
                  disabled={!!lockFields?.zip_code}
                />
              </FormLayout>
            </Flex>

            <Flex gap="2" width="100%">
              <Button variant="Secondary" onClick={handleModalCancel} fullWidth>
                Cancel
              </Button>
              <Button variant="Primary" onClick={handleSaveAddress} fullWidth>
                Save address
              </Button>
            </Flex>
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};
